if (!customElements.get('size-qty-picker')) {
  class SizeQtyPicker extends HTMLElement {
    static TIERS = [
      { min: 10, pct: 5,  pos: 25 },
      { min: 25, pct: 10, pos: 55 },
      { min: 50, pct: 20, pos: 100 },
    ];

    static SEGMENTS = [
      { from: 0,  to: 10, posFrom: 0,   posTo: 25  },
      { from: 10, to: 25, posFrom: 25,  posTo: 55  },
      { from: 25, to: 50, posFrom: 55,  posTo: 100 },
    ];

    connectedCallback() {
      this.sizeOptionIndex  = parseInt(this.dataset.sizeOptionIndex);
      this.colorOptionIndex = parseInt(this.dataset.colorOptionIndex);
      this.currency = this.dataset.currency || 'GBP';

      this.fill    = this.querySelector('.sqp-track__fill');
      this.nextMsg = this.querySelector('.sqp-next-msg');
      this.errorEl = this.querySelector('.sqp-error');
      this.addBtn  = this.querySelector('.sqp-add-btn');
      this.markers = this.querySelectorAll('.sqp-marker');
      this.cards   = this.querySelectorAll('.sqp-card');
      this.formEl  = document.getElementById(this.dataset.productFormId);

      // Variant IDs belonging to this product (for filtering cart line items)
      this.variantIds = new Set(
        [...this.cards].map(c => parseInt(c.dataset.variantId))
      );
      this.cartQty = 0;

      // Add body class for the CSS selector that hides the variant-picker's Size fieldset
      document.body.classList.add('has-size-qty-picker');

      this.addEventListener('input', this._onInput.bind(this));
      this.addEventListener('click', this._onClick.bind(this));
      this.addBtn.addEventListener('click', this._addToCart.bind(this));

      // Listen for color variant changes (variant-picker dispatches on document via bubbles)
      this._onVariantChange = this._onVariantChange.bind(this);
      document.addEventListener('on:variant:change', this._onVariantChange);

      // Re-sync cart qty when cart drawer edits line items
      this._onLineItemChange = this._syncCartQty.bind(this);
      document.addEventListener('on:line-item:change', this._onLineItemChange);

      // Initial filter — if a color is already selected, filter to that color
      if (this.colorOptionIndex >= 0) {
        const initialColor = this._getInitialSelectedColor();
        this._filterByColor(initialColor);
      } else {
        this._filterByColor(null);
      }

      // Fetch initial cart state to seed tier bar
      this._syncCartQty();
    }

    disconnectedCallback() {
      document.removeEventListener('on:variant:change', this._onVariantChange);
      document.removeEventListener('on:line-item:change', this._onLineItemChange);
      document.body.classList.remove('has-size-qty-picker');
    }

    // ---------------------------------------------------------------
    // Cart sync
    // ---------------------------------------------------------------

    _syncCartQty() {
      fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(r => r.json())
        .then(cart => {
          this.cartQty = (cart.items || [])
            .filter(i => this.variantIds.has(i.id))
            .reduce((s, i) => s + i.quantity, 0);
          this._update();
        })
        .catch(() => { /* leave cartQty as-is */ });
    }

    // ---------------------------------------------------------------
    // Event handlers
    // ---------------------------------------------------------------

    _onInput(evt) {
      if (!evt.target.matches('.sqp-qty__input')) return;
      const input = evt.target;
      const min = parseInt(input.min) || 0;
      const max = parseInt(input.max);
      let val = parseInt(input.value) || 0;
      if (val < min) val = min;
      if (max && val > max) val = max;
      input.value = val;
      this._update();
    }

    _onClick(evt) {
      const btn = evt.target.closest('.sqp-qty__btn');
      if (!btn) return;
      const input = btn.closest('.sqp-qty').querySelector('.sqp-qty__input');
      if (!input || input.disabled) return;
      const step = btn.dataset.dir === 'plus' ? 1 : -1;
      const min  = parseInt(input.min) || 0;
      const max  = parseInt(input.max);
      const next = (parseInt(input.value) || 0) + step;
      if (next < min || (max && next > max)) return;
      input.value = next;
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }

    _onVariantChange(evt) {
      if (this.colorOptionIndex < 0) return;
      const selectedOptions = evt.detail && evt.detail.selectedOptions;
      if (!selectedOptions) return;
      const colorValue = selectedOptions[this.colorOptionIndex];
      this._filterByColor(colorValue);
    }

    // ---------------------------------------------------------------
    // Color filtering
    // ---------------------------------------------------------------

    _getInitialSelectedColor() {
      // Try to read the checked color radio in the variant-picker
      const variantPicker = document.querySelector('variant-picker');
      if (!variantPicker) return null;
      const selectors = variantPicker.querySelectorAll('.option-selector');
      if (this.colorOptionIndex >= selectors.length) return null;
      const colorSelector = selectors[this.colorOptionIndex];
      const checked = colorSelector.querySelector('input:checked');
      return checked ? checked.value : null;
    }

    _filterByColor(color) {
      this.cards.forEach(card => {
        let show = true;
        if (color !== null && this.colorOptionIndex >= 0) {
          try {
            const options = JSON.parse(card.dataset.options);
            show = options[this.colorOptionIndex] === color;
          } catch (_) {
            show = true;
          }
        }
        card.classList.toggle('sqp-card--hidden', !show);
      });
      this._update();
    }

    // ---------------------------------------------------------------
    // Tier bar + UI update
    // ---------------------------------------------------------------

    _update() {
      const qty  = this._totalQty();
      const tier = this._activeTier(qty);
      const pct  = tier ? tier.pct : 0;

      this._updateBar(qty);
      this._updateMarkers(qty);
      this._updatePrices(pct);
      this._updateMessage(qty, pct);
      this.addBtn.disabled = qty === 0;
    }

    _totalQty() {
      const pickerQty = [...this.querySelectorAll('.sqp-qty__input')]
        .reduce((sum, i) => sum + (parseInt(i.value) || 0), 0);
      return pickerQty + this.cartQty;
    }

    _activeTier(qty) {
      return [...SizeQtyPicker.TIERS].reverse().find(t => qty >= t.min) || null;
    }

    _updateBar(qty) {
      const seg = SizeQtyPicker.SEGMENTS.find(s => qty >= s.from && qty < s.to);
      let pos;
      if (qty >= 50) {
        pos = 100;
      } else if (seg) {
        const range = seg.to - seg.from;
        const progress = (qty - seg.from) / range;
        pos = seg.posFrom + (seg.posTo - seg.posFrom) * progress;
      } else {
        pos = 0;
      }
      this.fill.style.width = pos + '%';
    }

    _updateMarkers(qty) {
      this.markers.forEach(m => {
        const min = parseInt(m.dataset.min);
        m.classList.toggle('sqp-marker--active', qty >= min);
      });
    }

    _updatePrices(pct) {
      this.cards.forEach(card => {
        const base = parseInt(card.dataset.price);
        const priceEl = card.querySelector('.sqp-card__price');
        if (pct > 0) {
          const discounted = Math.round(base * (100 - pct) / 100);
          priceEl.innerHTML =
            `<span class="sqp-card__price-now">${this._money(discounted)}</span>` +
            `<s class="sqp-card__price-was">${this._money(base)}</s>`;
        } else {
          priceEl.innerHTML =
            `<span class="sqp-card__price-now">${this._money(base)}</span>`;
        }
      });
    }

    _updateMessage(qty, pct) {
      if (qty === 0) {
        this.nextMsg.textContent = '';
        return;
      }
      const nextTier = SizeQtyPicker.TIERS.find(t => qty < t.min);
      if (nextTier) {
        const needed = nextTier.min - qty;
        this.nextMsg.textContent = `Add ${needed} more to unlock ${nextTier.pct}% off`;
      } else {
        this.nextMsg.textContent = `You're getting ${pct}% off`;
      }
    }

    _money(cents) {
      const symbol = this.currency === 'GBP' ? '£' : this.currency === 'USD' ? '$' : this.currency === 'EUR' ? '€' : '';
      return symbol + (cents / 100).toFixed(2);
    }

    // ---------------------------------------------------------------
    // Add to cart
    // ---------------------------------------------------------------

    // Forwards any properties[Name] fields a third-party app (e.g. Infinite
    // Options) injects into the surrounding product form, since this
    // component builds its own JSON body and would otherwise silently drop them.
    _collectExtraProperties() {
      const properties = {};
      if (!this.formEl) return properties;

      const reserved = new Set(['id', 'form_type', 'utf8', 'quantity']);
      for (const [key, value] of new FormData(this.formEl)) {
        if (reserved.has(key)) continue;
        const match = key.match(/^properties\[(.+)\]$/);
        if (match) properties[match[1]] = value;
      }
      return properties;
    }

    _addToCart() {
      const items = [...this.querySelectorAll('.sqp-qty__input')]
        .filter(i => parseInt(i.value) > 0)
        .map(i => ({ id: parseInt(i.dataset.variantId), quantity: parseInt(i.value) }));

      if (!items.length) return;

      const properties = this._collectExtraProperties();
      if (Object.keys(properties).length) {
        items.forEach(item => { item.properties = properties; });
      }

      this.errorEl.setAttribute('hidden', '');
      this.addBtn.disabled = true;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items }),
      })
        .then(r => r.ok ? r.json() : r.json().then(err => Promise.reject(err)))
        .then(() => {
          // Optimistic cart-qty update so tier bar stays accurate immediately
          const addedQty = items.reduce((s, i) => s + i.quantity, 0);
          this.cartQty += addedQty;
          // Reset all inputs
          this.querySelectorAll('.sqp-qty__input').forEach(i => { i.value = 0; });
          this._update();
          // Notify other listeners (cart count, etc.)
          document.dispatchEvent(new CustomEvent('on:cart:add'));
          document.dispatchEvent(new CustomEvent('on:line-item:change'));
          // Refresh cart drawer contents, then open it
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
        })
        .catch(err => {
          this.errorEl.textContent = (err && err.description) || 'Could not add to cart. Please try again.';
          this.errorEl.removeAttribute('hidden');
        })
        .finally(() => {
          this.addBtn.disabled = this._totalQty() === 0;
        });
    }
  }

  customElements.define('size-qty-picker', SizeQtyPicker);
}
